import { useState } from "react";
import { object, string } from "yup";
import TextField from "@mui/material/TextField";
import { when } from "util/support";
import { useForm } from "hooks";
import { FormAdapter } from "hooks/useForm";
import { ConfirmatableDialog } from "ui/molecules/Dialog";
import { ReviewableAction, useActionContext } from "../../providers";
import type { DialogProps } from "./types";

const Reject = ({
    isOpen,
    isProcessing: isLoading,
    renderLabel,
    onConfirm,
    onClose
}: DialogProps) => {
    const [isProcessing, setProcessing] = useState(false);

    const actions = useActionContext();

    const close = () => (
        when(
            Boolean(onClose),
            onClose,
            () => actions.reset(ReviewableAction.Reject)
        )
    );

    const {
        isValid,
        values,
        handleBlur,
        handleChange,
        submitForm
    } = useForm({
        isInitialValid: false,
        initialValues: {
            reason: ''
        },
        validationSchema: object().shape({
            reason: string()
                .trim()
                .required()
        }),
        onSaveOrCreate: async ({ reason }, helpers) => {
            await onConfirm({ notes: String(reason) });

            helpers!.resetForm();

            return FormAdapter.fromResponse({});
        }
    });

    const handleConfirm = async () => {
        setProcessing(true);

        await submitForm();

        close();
        setProcessing(false);
    };

    const content = {
        dialogContent: [
            `Are you sure that you would like to reject ${renderLabel()}?`,
            <TextField
                multiline
                fullWidth
                required
                minRows={3}
                variant='outlined'
                label='Reason for rejection'
                name='reason'
                value={values.reason}
                onBlur={handleBlur}
                onChange={handleChange}
            />
        ],
        actionContent: 'reject'
    };

    return (
        <ConfirmatableDialog
            isProcessing={isLoading ?? isProcessing}
            open={isOpen ?? actions.hasAction(ReviewableAction.Reject)}
            checkPredicate={() => false}
            content={[
                content,
                content
            ]}
            onConfirm={handleConfirm}
            onCancel={close}
            slotProps={{
                confirmAction: {
                    disabled: !isValid,
                    color: 'error'
                }
            }}
        />
    );
};

export default Reject;
